import React, { useState, useEffect } from 'react';
import api from '@/api/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate } from '../../utils/dateTimeUtils';
import { 
  Link, Copy, Eye, Trash2, Plus, Search, 
  CheckCircle, XCircle, Clock, MessageCircle
} from 'lucide-react';

export default function PaymentLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  
  const [formData, setFormData] = useState({
    student_id: '',
    amount: '',
    description: '',
    due_date: '',
    fee_type: 'fees',
    valid_days: 7
  });

  useEffect(() => {
    fetchLinks();
    fetchStudents();
  }, [statusFilter]);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/payment-links/?status=${statusFilter}`);
      setLinks(response.data);
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students');
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.student_id || !formData.amount) {
      toast.error('Student and Amount are required');
      return;
    }

    try {
      await api.post('/payment-links/generate', formData);
      toast.success('Payment link generated!');
      setShowModal(false);
      resetForm();
      fetchLinks();
    } catch (error) {
      console.error('Error generating link:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate link');
    }
  };

  const handleDeactivate = async (linkId) => {
    if (!window.confirm('Deactivate this payment link?')) return;
    
    try {
      await api.delete(`/payment-links/${linkId}`);
      toast.success('Link deactivated');
      fetchLinks();
    } catch (error) {
      toast.error('Failed to deactivate');
    }
  };

  const handleSendReminder = async (link) => {
    try {
      const response = await api.post(`/payment-links/send-reminder/${link.id}?channel=whatsapp`);
      const data = response.data;
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/91${data.phone}?text=${encodeURIComponent(data.text_message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('WhatsApp opened with payment reminder');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const copyLink = (linkCode) => {
    const tenantCode = localStorage.getItem('tenant_code') || '';
    const paymentUrl = `${window.location.origin}/${tenantCode}/pay/${linkCode}`;
    
    navigator.clipboard.writeText(paymentUrl);
    toast.success('Payment link copied!');
  };

  const resetForm = () => {
    setFormData({
      student_id: '',
      amount: '',
      description: '',
      due_date: '',
      fee_type: 'fees',
      valid_days: 7
    });
    setSearchTerm('');
  };

  const getStatusBadge = (link) => {
    if (link.is_paid) {
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
        <CheckCircle size={12} /> Paid
      </span>;
    }
    
    const expiresAt = new Date(link.expires_at);
    if (!link.is_active || expiresAt < new Date()) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs flex items-center gap-1">
        <XCircle size={12} /> Expired
      </span>;
    }
    
    return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1">
      <Clock size={12} /> Active
    </span>;
  };

  const filteredStudents = students.filter(s => 
    s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.admission_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Payment Links</h1>
          <p className="text-gray-600 text-sm">Generate and share payment links with students</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="flex items-center gap-2">
          <Plus size={18} />
          Generate Link
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex gap-2">
          {['active', 'paid', 'expired', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Links Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : links.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Link className="mx-auto mb-3 text-gray-400" size={48} />
            <p>No payment links found</p>
            <Button onClick={() => setShowModal(true)} className="mt-4">
              Generate First Link
            </Button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Student</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Created</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Views</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {links.map(link => (
                <tr key={link.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-800">{link.student_name}</div>
                      <div className="text-xs text-gray-500">{link.admission_number}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-gray-800">₹{link.amount?.toLocaleString('en-IN')}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                    {link.description || '-'}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(link)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(link.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Eye size={14} /> {link.views_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => copyLink(link.link_code)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Copy Link"
                      >
                        <Copy size={16} />
                      </button>
                      {!link.is_paid && link.is_active && (
                        <>
                          <button
                            onClick={() => handleSendReminder(link)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Send WhatsApp"
                          >
                            <MessageCircle size={16} />
                          </button>
                          <button
                            onClick={() => handleDeactivate(link.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Deactivate"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Generate Link Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Generate Payment Link</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Student Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Student *
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    type="text"
                    placeholder="Search student..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {searchTerm && (
                  <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg">
                    {filteredStudents.slice(0, 10).map(student => (
                      <div
                        key={student.id}
                        onClick={() => {
                          setFormData({ ...formData, student_id: student.id });
                          setSearchTerm(student.full_name);
                        }}
                        className={`p-2 cursor-pointer hover:bg-gray-50 ${
                          formData.student_id === student.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="font-medium">{student.full_name}</div>
                        <div className="text-xs text-gray-500">{student.admission_number}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (₹) *
                </label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  placeholder="Enter amount"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Semester 1 Fee"
                />
              </div>

              {/* Fee Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fee Type
                </label>
                <select
                  value={formData.fee_type}
                  onChange={(e) => setFormData({ ...formData, fee_type: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="fees">Tuition Fee</option>
                  <option value="admission">Admission Fee</option>
                  <option value="exam">Exam Fee</option>
                  <option value="hostel">Hostel Fee</option>
                  <option value="transport">Transport Fee</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Valid Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Valid For (Days)
                </label>
                <Input
                  type="number"
                  value={formData.valid_days}
                  onChange={(e) => setFormData({ ...formData, valid_days: parseInt(e.target.value) })}
                  min="1"
                  max="30"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  Generate Link
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
